import React, { useCallback, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import {
  NotificationService,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import '../../../../../prop-types/add-on.types';
import styles from './style.module.less';
import { getUpsellModalContent } from '../../../../../components/upsell-modal/helper';
import { getSeller } from '../../../../../store/seller/seller.actions';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { WorkflowSteps, useTrackWorkflow } from '../../../../../tracking/use-workflow-tracking';
import { disableAddOn } from '../../../api/add-ons-api';
import messages from '../messages';

/**
 * @typedef {import('../../../../../prop-types/add-on.types').AddOnPropType} AddOn
 */

/**
 * @param {object} props
 * @param {AddOn} props.addOn
 * @param {() => void} props.onCloseModal
 * @param {(code: string) => void} props.onError
 * @returns {JSX.Element}
 */
export const DeactivateAddOnButton = ({ onCloseModal, addOn, onError = noop }) => {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const dispatch = useDispatch();
  const intl = useIntl();
  const content = getUpsellModalContent(addOn.code);
  const trackAddOnWorkflow = useTrackWorkflow(Workflows.ADD_ON_FLOW);

  const addOnName = intl.formatMessage(content.name);

  const onClickDeactivateAddon = useCallback(async () => {
    try {
      setIsDeactivating(true);
      await disableAddOn(addOn.code);
      trackAddOnWorkflow(WorkflowSteps.COMPLETE);
      dispatch(getSeller());
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage values={{ addOnName }} {...messages.deactivateAddOnSuccessNotificationTitle} />,
        content: <FormattedMessage values={{ addOnName }} {...messages.deactivateAddOnSuccessNotificationText} />,
        duration: NOTIFICATION_DURATIONS.LONG,
      });
    } catch (e) {
      trackAddOnWorkflow(WorkflowSteps.ERROR, { error: e });
      onError(addOn.code);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage values={{ addOnName }} {...messages.deactivateAddOnFailureNotificationTitle} />,
        content: <FormattedMessage values={{ addOnName }} {...messages.deactivateAddOnFailureNotificationText} />,
        buttons: [
          {
            text: <FormattedMessage {...messages.retryButton} />,
            callback: onClickDeactivateAddon,
          },
        ],
      });
    }
    setIsDeactivating(false);
    onCloseModal();
  }, [addOnName, onCloseModal, addOn.code, onError, dispatch, trackAddOnWorkflow]);

  return (
    <Button className={styles['deactivate-addon-button ']} disabled={isDeactivating} onClick={onClickDeactivateAddon} theme='danger'>
      <Text textType={TextType.Paragraph} value={messages.deactivateAddOn} />
    </Button>
  );
};
